/* eslint-disable max-lines */

export default class TidioController {
  constructor(options = {}) {
    this.options = {
      selector: options.selector || "#tidio-chat-iframe",
      targetPosition: options.targetPosition || "15px",
      chatOpenPosition: options.chatOpenPosition || "0px",
      earlyDetectionInterval: options.earlyDetectionInterval || 50,
      maxEarlyChecks: options.maxEarlyChecks || 50,
      debounceMs: options.debounceMs || 50,
      onLoaded: options.onLoaded || null,
      onReady: options.onReady || null,
      onOpen: options.onOpen || null,
      onClose: options.onClose || null,
      originalBottomPosition: null,
      ...options
    };

    this.initializeState();
    this.bindMethods();
  }

  initializeState() {
    this.cache = {
      iframe: null,
      lastPosition: null,
      isPositioning: false,
      hasInitialized: false,
      isChatOpen: false,
      isLoaded: false
    };

    this.timers = {
      debounce: null,
      earlyDetection: null,
      loadMonitoring: null
    };

    this.eventHandlers = {
      tidioReady: null,
      tidioOpen: null,
      tidioClose: null
    };
  }

  bindMethods() {
    this.handleTidioReady = this.handleTidioReady.bind(this);
    this.handleTidioOpen = this.handleTidioOpen.bind(this);
    this.handleTidioClose = this.handleTidioClose.bind(this);
    this.setPosition = this.setPosition.bind(this);
  }

  initialize() {
    this.startEarlyDetection();
    this.setupTidioEventListeners();
    return this;
  }

  isLoaded() {
    return this.cache.isLoaded;
  }

  isChatOpen() {
    return this.cache.isChatOpen;
  }

  getIframe() {
    return this.cache.iframe;
  }

  setPosition(offset) {
    if (!this.cache.iframe || !document.body.contains(this.cache.iframe)) {
      return false;
    }

    if (this.cache.isChatOpen) {
      return false;
    }

    const position = this.normalizePosition(offset);

    if (this.timers.debounce) {
      clearTimeout(this.timers.debounce);
    }

    this.timers.debounce = setTimeout(() => {
      this.cache.iframe.style.bottom = position;
      this.cache.lastPosition = position;
    }, this.options.debounceMs);

    return true;
  }

  resetPosition() {
    return this.setPosition(this.options.targetPosition);
  }

  show() {
    if (this.cache.iframe && document.body.contains(this.cache.iframe)) {
      this.cache.iframe.style.display = "";
      return true;
    }
    return false;
  }

  hide() {
    if (this.cache.iframe && document.body.contains(this.cache.iframe)) {
      this.cache.iframe.style.display = "none";
      return true;
    }
    return false;
  }

  getCurrentPosition() {
    if (this.cache.iframe && document.body.contains(this.cache.iframe)) {
      return this.cache.iframe.style.bottom || this.options.targetPosition;
    }
    return null;
  }

  startEarlyDetection() {
    let checkCount = 0;

    const performCheck = () => {
      const iframe = document.querySelector(this.options.selector);
      if (iframe) {
        this.handleTidioDetected(iframe);
        return true;
      }
      return false;
    };

    if (performCheck()) return;

    this.timers.earlyDetection = setInterval(() => {
      checkCount += 1;
      if (performCheck() || checkCount >= this.options.maxEarlyChecks) {
        clearInterval(this.timers.earlyDetection);
        this.timers.earlyDetection = null;
      }
    }, this.options.earlyDetectionInterval);
  }

  handleTidioDetected(iframe) {
    this.cache.iframe = iframe;
    this.cache.isLoaded = true;
    this.cache.hasInitialized = true;

    this.options.originalBottomPosition = this.cache.iframe.style.bottom;

    this.setPosition(this.options.targetPosition);
    if (this.options.onLoaded) {
      this.options.onLoaded(iframe);
    }
  }

  setupTidioEventListeners() {
    this.eventHandlers.tidioReady = this.handleTidioReady;
    document.addEventListener("tidioChat-ready", this.eventHandlers.tidioReady, {
      passive: true
    });
    this.setupTidioApiListeners();
  }

  setupTidioApiListeners() {
    const checkApiAvailability = () => {
      if (window.tidioChatApi?.on) {
        this.eventHandlers.tidioOpen = this.handleTidioOpen;
        this.eventHandlers.tidioClose = this.handleTidioClose;

        window.tidioChatApi.on("open", this.eventHandlers.tidioOpen);
        window.tidioChatApi.on("close", this.eventHandlers.tidioClose);

        if (this.options.onReady) {
          this.options.onReady();
        }
      } else {
        setTimeout(checkApiAvailability, 100);
      }
    };

    checkApiAvailability();
  }

  handleTidioReady() {
    if (!this.cache.iframe) {
      const iframe = document.querySelector(this.options.selector);
      if (iframe) {
        this.handleTidioDetected(iframe);
      }
    }
  }

  handleTidioOpen() {
    this.cache.isChatOpen = true;
    if (this.cache.iframe && document.body.contains(this.cache.iframe)) {
      this.cache.iframe.style.bottom = this.options.chatOpenPosition;
    }

    if (typeof this.options.onOpen === "function") {
      this.options.onOpen();
    }
  }

  handleTidioClose() {
    this.cache.isChatOpen = false;

    this.resetPosition();

    if (this.options.onClose) {
      this.options.onClose();
    }
  }

  normalizePosition(offset) {
    if (typeof offset === "number") {
      return `${offset}px`;
    }
    if (typeof offset === "string") {
      return offset;
    }
    return this.options.targetPosition;
  }

  destroy() {
    this.cleanupTimers();
    this.cleanupEventListeners();
    this.resetToDefault();
    this.clearCache();
  }

  cleanupTimers() {
    Object.values(this.timers).forEach(timer => {
      if (timer) {
        clearTimeout(timer);
        clearInterval(timer);
      }
    });
    Object.keys(this.timers).forEach(key => {
      this.timers[key] = null;
    });
  }

  cleanupEventListeners() {
    if (this.eventHandlers.tidioReady) {
      document.removeEventListener("tidioChat-ready", this.eventHandlers.tidioReady);
    }

    if (window.tidioChatApi?.off) {
      if (this.eventHandlers.tidioOpen) {
        window.tidioChatApi.off("open", this.eventHandlers.tidioOpen);
      }
      if (this.eventHandlers.tidioClose) {
        window.tidioChatApi.off("close", this.eventHandlers.tidioClose);
      }
    }
    Object.keys(this.eventHandlers).forEach(key => {
      this.eventHandlers[key] = null;
    });
  }

  resetToDefault() {
    if (this.cache.iframe && document.body.contains(this.cache.iframe)) {
      this.cache.iframe.style.bottom = this.options.originalBottomPosition;
      this.cache.iframe.style.display = "";
    }
  }

  clearCache() {
    Object.keys(this.cache).forEach(key => {
      this.cache[key] = typeof this.cache[key] === "boolean" ? false : null;
    });
  }
}
